import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAsync } from 'react-use';
import { useToast, toastVariant } from 'components/ToastProvider';

import TeamMemberService from 'services/TeamMemberService';

import {
  translateMessageResponse,
  getTeamMemberValue,
  getSelectedSummary,
} from 'components/MessageBuilder/utils';

import { displayNameToOption } from 'services/helpers';
import { useEmailFromDomainEnabledEntitlement } from 'hooks/useEmailFromDomainEnabledEntitlement';

import { SENDER_TYPE } from 'components/MessageBuilder/components';

const { TEAM_MEMBER, BUSINESS } = SENDER_TYPE;

export const useDuplicateEmail = (
  initialData, // email
  onConfirm,
  values,
  isPasted = false,
  onChange
) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const convertedRef = useRef(false);
  const emailFromDomainEnabled = useEmailFromDomainEnabledEntitlement();

  const {
    sender,
    subject,
    name,
    craftJsContent,
    craftJson,
    senderTeamMember,
    externalAccount,
    htmlContent,
  } = initialData || {};

  const {
    id: teamMemberId,
    first_name,
    last_name,
    email,
    display_name,
  } = useSelector((s) => s.authentication.teamMember);

  const chosenBusinessName = useSelector(
    ({ authentication }) => authentication?.chosenBusiness?.name ?? ''
  );

  const {
    currentTeamMember,
    integratedInbox,
    customName,
    subjectLine,
    internalName,
    senderType,
    sendFrom,
    sendFromTeamMemberField,
  } = useMemo(
    () => ({
      currentTeamMember: {
        id: teamMemberId,
        firstName: first_name,
        lastName: last_name,
        email,
        displayName: display_name,
      },
      integratedInbox: externalAccount
        ? {
            ...externalAccount,
            label: externalAccount?.label || externalAccount?.email,
            value: externalAccount?.value || externalAccount?.id,
          }
        : null,
      customName: sender?.customName || '',
      subjectLine: subject || '',
      internalName: name || '',
      senderType:
        sender?.type || (emailFromDomainEnabled ? TEAM_MEMBER : BUSINESS),
      sendFrom: sender?.id || teamMemberId,
      sendFromTeamMemberField: initialData?.senderField?.displayName
        ? displayNameToOption(initialData.senderField)
        : initialData.senderField || null,
    }),
    [
      display_name,
      email,
      emailFromDomainEnabled,
      externalAccount,
      first_name,
      initialData.senderField,
      last_name,
      name,
      sender?.customName,
      sender?.id,
      sender?.type,
      subject,
      teamMemberId,
    ]
  );

  const { value: rolesOptions } = useAsync(
    TeamMemberService.getRolesOptionsList,
    []
  );

  const { value: translated } = useAsync(async () => {
    if (values) {
      if (isPasted) {
        if (!convertedRef.current && rolesOptions) {
          const teamMemberValue = getTeamMemberValue(
            sendFrom,
            senderTeamMember,
            currentTeamMember,
            undefined
          );

          const selectedSummary = getSelectedSummary(
            senderType,
            [teamMemberValue],
            rolesOptions,
            chosenBusinessName,
            sendFrom,
            sendFromTeamMemberField,
            integratedInbox
          );

          const response = translateMessageResponse(
            craftJsContent || craftJson,
            {},
            htmlContent,
            {
              senderType,
              sendFrom,
              customName,
              subjectLine,
              internalName,
              selectedSummary,
              integratedInbox,
            }
          );

          convertedRef.current = true;

          try {
            await onConfirm(
              { ...response, baseMessageId: initialData?.baseMessageId },
              false
            );
            showToast({
              message: t('Message Template was saved.'),
              variant: toastVariant.SUCCESS,
            });
          } catch (error) {
            showToast({
              message: t(
                'Message Template could not be saved. Please try again or contact Kizen support.'
              ),
              variant: toastVariant.FAILURE,
            });
          }

          return true;
        } else {
          // waiting to convert / converted
          return convertedRef.current;
        }
      } else {
        // no paste
        return true;
      }
    } else {
      // no step
      return false;
    }
  }, [values, isPasted, convertedRef.current, rolesOptions]);

  if (translated && !values.translated) {
    setTimeout(() => {
      onChange({ ...values, translated });
    }, 1);
  }

  return translated || false;
};
